<template>
  <span
    class="iq-thinking-pulsating-icon"
    :class="rootClasses"
    :style="rootStyle"
    role="img"
    :aria-label="ariaLabel"
  >
    <span v-if="showHalos" class="iq-thinking-pulsating-icon__halos" aria-hidden="true">
      <span class="iq-thinking-pulsating-icon__halo" />
      <span class="iq-thinking-pulsating-icon__halo" />
    </span>

    <svg
      class="iq-thinking-pulsating-icon__mark"
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect width="24" height="24" rx="4.5" fill="#ED2738" />
      <path
        d="M4.5 6.2168H6.63255V17.0341H4.5V6.2168Z"
        fill="white"
      />
      <path
        d="M19.3673 11.625C19.3673 13.0261 18.9243 14.2417 18.0383 15.2719L16.4621 16.5546C15.6276 17.0182 14.721 17.25 13.7423 17.25C12.1867 17.25 10.8629 16.7091 9.77086 15.6274C8.67883 14.5353 8.13281 13.2012 8.13281 11.625C8.13281 10.0488 8.67883 8.71977 9.77086 7.63804C10.8629 6.54601 12.1867 6 13.7423 6C15.298 6 16.6218 6.54601 17.7138 7.63804C18.8162 8.71977 19.3673 10.0488 19.3673 11.625Z"
        fill="white"
      />
      <path
        class="iq-thinking-pulsating-icon__q-tail"
        d="M18.0383 15.2719L19.151 16.4309L17.6366 17.7754"
        stroke="white"
        stroke-width="2.18"
        stroke-linecap="round"
        stroke-linejoin="round"
        fill="none"
        @animationend="onTailFlashEnd"
      />
    </svg>
  </span>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";

const props = defineProps({
  state: {
    type: String,
    default: "idle",
    validator: (value) => ["idle", "thinking", "done"].includes(value),
  },
  size: {
    type: Number,
    default: 24,
    validator: (value) => [16, 24, 56].includes(value),
  },
});

const emit = defineEmits(["done-complete"]);

const isTailFlashing = ref(false);
const prefersReducedMotion = ref(false);
let reducedMotionMediaQuery;

function readReducedMotionPreference() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function syncReducedMotionPreference() {
  prefersReducedMotion.value = readReducedMotionPreference();
}

onMounted(() => {
  syncReducedMotionPreference();
  if (typeof window.matchMedia !== "function") return;
  reducedMotionMediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  reducedMotionMediaQuery.addEventListener("change", syncReducedMotionPreference);
});

onUnmounted(() => {
  reducedMotionMediaQuery?.removeEventListener("change", syncReducedMotionPreference);
});

const showHalos = computed(() => props.state === "thinking" && !prefersReducedMotion.value);

const rootClasses = computed(() => [
  `iq-thinking-pulsating-icon--size-${props.size}`,
  {
    "iq-thinking-pulsating-icon--thinking":
      props.state === "thinking" && !prefersReducedMotion.value,
    "iq-thinking-pulsating-icon--idle": props.state !== "thinking",
    "iq-thinking-pulsating-icon--done-flash": isTailFlashing.value,
  },
]);

const rootStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
}));

const ariaLabel = computed(() => {
  if (props.state === "thinking") return "exIQtive AI is thinking";
  if (props.state === "done") return "exIQtive AI finished thinking";
  return "exIQtive AI";
});

watch(
  () => props.state,
  (nextState) => {
    if (nextState === "done") {
      if (prefersReducedMotion.value) {
        isTailFlashing.value = false;
        emit("done-complete");
        return;
      }
      isTailFlashing.value = true;
      return;
    }
    isTailFlashing.value = false;
  },
  { immediate: true }
);

function onTailFlashEnd(event) {
  if (event.animationName !== "iq-thinking-q-tail-flash") return;
  isTailFlashing.value = false;
  emit("done-complete");
}
</script>

<style scoped>
.iq-thinking-pulsating-icon {
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  overflow: visible;
}

.iq-thinking-pulsating-icon__halos {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.iq-thinking-pulsating-icon__halo {
  position: absolute;
  border-radius: 9999px;
  border: 1px solid rgba(237, 39, 56, 0.45);
  box-sizing: border-box;
}

.iq-thinking-pulsating-icon--size-16 .iq-thinking-pulsating-icon__halo {
  width: 22px;
  height: 22px;
}

.iq-thinking-pulsating-icon--size-24 .iq-thinking-pulsating-icon__halo {
  width: 32px;
  height: 32px;
}

.iq-thinking-pulsating-icon--size-56 .iq-thinking-pulsating-icon__halo {
  width: 72px;
  height: 72px;
}

.iq-thinking-pulsating-icon__mark {
  position: relative;
  z-index: 1;
  display: block;
  width: 100%;
  height: 100%;
  overflow: visible;
  transform-origin: center center;
}

.iq-thinking-pulsating-icon--idle .iq-thinking-pulsating-icon__mark {
  filter: var(--iq-thinking-idle-shadow);
}

.iq-thinking-pulsating-icon--size-16 {
  --iq-thinking-idle-shadow: drop-shadow(0 1px 2px rgba(16, 24, 40, 0.06));
}

.iq-thinking-pulsating-icon--size-24 {
  --iq-thinking-idle-shadow: drop-shadow(0 2px 4px rgba(16, 24, 40, 0.08));
}

.iq-thinking-pulsating-icon--size-56 {
  --iq-thinking-idle-shadow: drop-shadow(0 4px 8px rgba(16, 24, 40, 0.1));
}

.iq-thinking-pulsating-icon--thinking .iq-thinking-pulsating-icon__mark {
  animation: iq-thinking-breathe 2.6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

.iq-thinking-pulsating-icon--thinking .iq-thinking-pulsating-icon__halo {
  animation: iq-thinking-pulse-glow 2.6s cubic-bezier(0.45, 0, 0.55, 1) infinite;
}

.iq-thinking-pulsating-icon--thinking .iq-thinking-pulsating-icon__halo:nth-child(2) {
  animation-delay: 1.3s;
}

.iq-thinking-pulsating-icon--done-flash .iq-thinking-pulsating-icon__q-tail {
  animation: iq-thinking-q-tail-flash 0.55s cubic-bezier(0.45, 0, 0.55, 1) forwards;
}

@keyframes iq-thinking-breathe {
  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.055);
  }
}

@keyframes iq-thinking-pulse-glow {
  0% {
    transform: scale(0.82);
    opacity: 0;
  }

  28% {
    opacity: 0.55;
  }

  100% {
    transform: scale(1.55);
    opacity: 0;
  }
}

@keyframes iq-thinking-q-tail-flash {
  0%,
  100% {
    filter: brightness(1);
  }

  50% {
    filter: brightness(2);
  }
}

@media (prefers-reduced-motion: reduce) {
  .iq-thinking-pulsating-icon__halos {
    display: none;
  }

  .iq-thinking-pulsating-icon--thinking .iq-thinking-pulsating-icon__mark,
  .iq-thinking-pulsating-icon--done-flash .iq-thinking-pulsating-icon__q-tail {
    animation: none;
  }
}
</style>
